// Mock data for Integration Mapping Configuration
// Importing directly from mock JSON files to ensure 100% accuracy

import mappingProfilesRaw from '../mocks/getFieldMappingStoragesList.json';
import fieldMappingsRaw from '../mocks/fieldMappings.json';
import targetFieldsRaw from '../mocks/targetFields.json';

// Transform mapping profiles to match expected format
export const mockMappingProfiles = mappingProfilesRaw.map(profile => ({
  id: profile.Id,
  name: profile.Storage_Name__c,
  isDefault: profile.IsDefault__c,
  isActive: profile.IsActive__c,
  createdAt: new Date(profile.CreatedDate).toISOString(),
  updatedAt: new Date(profile.LastModifiedDate).toISOString(),
  createdBy: profile.CreatedBy?.Name,
  createdById: profile.CreatedById,
}));

// Use target fields directly from JSON
export const mockTargetFields = targetFieldsRaw;

// Transform field mappings to be organized by profile ID
// The fieldMappings.json contains mappings for the default profile
// We'll clone this data for all profiles so they all start with the same mappings
export const mockFieldMappings = {};

// Assign field mappings to all profiles (deep clone to avoid reference issues)
mockMappingProfiles.forEach(profile => {
  // Deep clone the field mappings array for each profile
  // This ensures each profile has its own independent copy of the mappings
  mockFieldMappings[profile.id] = JSON.parse(JSON.stringify(fieldMappingsRaw));
});

// Mock source fields (Salesforce Opportunity fields)
// This is a simplified list - in production, this would come from Salesforce API
export const mockSourceFields = [
  {
    apiName: "Name",
    label: "Opportunity Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "AccountId",
    label: "Account",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "Account.Name",
    label: "Account Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.Website",
    label: "Account Website",
    type: "URL",
    isCustom: false,
  },
  {
    apiName: "Account.BillingStreet",
    label: "Billing Street",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.BillingCity",
    label: "Billing City",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.BillingPostalCode",
    label: "Billing Postal Code",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.BillingCountry",
    label: "Billing Country",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.BillingState",
    label: "Billing State",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Account.Industry",
    label: "Account Industry",
    type: "PICKLIST",
    isCustom: false,
  },
  {
    apiName: "Account.AnnualRevenue",
    label: "Annual Revenue",
    type: "CURRENCY",
    isCustom: false,
  },
  {
    apiName: "Account.NumberOfEmployees",
    label: "Number of Employees",
    type: "NUMBER",
    isCustom: false,
  },
  {
    apiName: "Account.Description",
    label: "Account Description",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "Amount",
    label: "Amount",
    type: "CURRENCY",
    isCustom: false,
  },
  {
    apiName: "CloseDate",
    label: "Close Date",
    type: "DATE",
    isCustom: false,
  },
  {
    apiName: "Type",
    label: "Opportunity Type",
    type: "PICKLIST",
    isCustom: false,
  },
  {
    apiName: "Description",
    label: "Description",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "NextStep",
    label: "Next Step",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "StageName",
    label: "Stage",
    type: "PICKLIST",
    isCustom: false,
  },
  {
    apiName: "LeadSource",
    label: "Lead Source",
    type: "PICKLIST",
    isCustom: false,
  },
  {
    apiName: "ContactId",
    label: "Contact",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "Contact.Name",
    label: "Contact Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Contact.Email",
    label: "Contact Email",
    type: "EMAIL",
    isCustom: false,
  },
  {
    apiName: "Contact.Phone",
    label: "Contact Phone",
    type: "PHONE",
    isCustom: false,
  },
  {
    apiName: "Probability",
    label: "Probability (%)",
    type: "NUMBER",
    isCustom: false,
  },
  {
    apiName: "ExpectedRevenue",
    label: "Expected Revenue",
    type: "CURRENCY",
    isCustom: false,
  },
  {
    apiName: "TotalOpportunityQuantity",
    label: "Quantity",
    type: "NUMBER",
    isCustom: false,
  },
  {
    apiName: "IsClosed",
    label: "Closed",
    type: "BOOLEAN",
    isCustom: false,
  },
  {
    apiName: "IsWon",
    label: "Won",
    type: "BOOLEAN",
    isCustom: false,
  },
  {
    apiName: "ForecastCategory",
    label: "Forecast Category",
    type: "PICKLIST",
    isCustom: false,
  },
  {
    apiName: "ForecastCategoryName",
    label: "Forecast Category Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "CampaignId",
    label: "Primary Campaign Source",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "HasOpportunityLineItem",
    label: "Has Line Items",
    type: "BOOLEAN",
    isCustom: false,
  },
  {
    apiName: "Pricebook2Id",
    label: "Price Book",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "OwnerId",
    label: "Owner",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "Owner.Name",
    label: "Owner Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "Owner.Email",
    label: "Owner Email",
    type: "EMAIL",
    isCustom: false,
  },
  {
    apiName: "CreatedDate",
    label: "Created Date",
    type: "DATETIME",
    isCustom: false,
  },
  {
    apiName: "CreatedById",
    label: "Created By",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "LastModifiedDate",
    label: "Last Modified Date",
    type: "DATETIME",
    isCustom: false,
  },
  {
    apiName: "LastModifiedById",
    label: "Last Modified By",
    type: "REFERENCE",
    isCustom: false,
  },
  {
    apiName: "LastActivityDate",
    label: "Last Activity Date",
    type: "DATE",
    isCustom: false,
  },
];

export const mockSubmenuSourceFieldsForDefault = [
  {
    apiName: "account_description",
    label: "Account Description",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "account_name",
    label: "Account Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "active_goal",
    label: "Active Goal",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "aws_account_id",
    label: "AWS Account ID",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "aws_code",
    label: "AWS Code",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "aws_name",
    label: "AWS Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "aws_tag",
    label: "AWS Tag",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "bd_status",
    label: "BD Status",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "billing_city",
    label: "Billing City",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "billing_country",
    label: "Billing Country",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "billing_state",
    label: "Billing State/Province",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "billing_street",
    label: "Billing Street",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "billing_zip",
    label: "Billing Zip/Postal Code",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "compaign_description",
    label: "Compaign Description",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "city",
    label: "City",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "comments",
    label: "Comments",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "datacomkey",
    label: "Data.com Key",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "duns_number",
    label: "Duns Number",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "established",
    label: "Established",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "for_ella",
    label: "For Ella",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "goal_id",
    label: "Goal ID",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "goal_name",
    label: "Goal Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "installed_developer_tools",
    label: "Installed Developer Tools",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "installed_enterprise_applications",
    label: "Installed Enterprise Applications",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "installed_infra_roviders",
    label: "Installed Infra Providers",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "installed_isv_technologies",
    label: "Installed ISV Technologies",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "isv_hl_strategy",
    label: "ISV HL Strategy",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "jigsaw_company_id",
    label: "Jigsaw Company ID",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "latest_data_source",
    label: "Latest Data Source",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "linkedin_company_profile",
    label: "LinkedIn Company Profile",
    type: "URL",
    isCustom: false,
  },
  {
    apiName: "marketing_status",
    label: "Marketing Status",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "microsoft_name",
    label: "Microsoft Name",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "microsoft_tags",
    label: "Microsoft Tags",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "ms_sales_id",
    label: "MS Sales ID",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "msft_internal_organization",
    label: "MSFT Internal Organization",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "msft_partnership_tier",
    label: "MSFT Partnership Tier",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "overall_status",
    label: "Overall Status",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "partnership_status",
    label: "Partnership Status",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "payment_terms",
    label: "Payment Terms",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "photo_url",
    label: "Photo URL",
    type: "URL",
    isCustom: false,
  },
  {
    apiName: "sales_status",
    label: "Sales Status",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "sfserver",
    label: "SFServer",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "shipping_city",
    label: "Shipping City",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "shipping_country",
    label: "Shipping Country",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "shipping_state",
    label: "Shipping State/Province",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "shipping_street",
    label: "Shipping Street",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "shipping_zip",
    label: "Shipping Zip/Postal Code",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "sip_description",
    label: "SIC Description",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "source_system_id",
    label: "Source System ID",
    type: "STRING",
    isCustom: false,
  },
  {
    apiName: "tca_input",
    label: "TCA Input",
    type: "TEXTAREA",
    isCustom: false,
  },
  {
    apiName: "website",
    label: "Website",
    type: "URL",
    isCustom: false,
  },
];

export const mockSubmenuSourceFieldsForAccount = mockSubmenuSourceFieldsForDefault;
export const mockSubmenuSourceFieldsForContact = mockSubmenuSourceFieldsForDefault;
export const mockSubmenuSourceFieldsForCampaign = mockSubmenuSourceFieldsForDefault;
export const mockSubmenuSourceFieldsForOwner = mockSubmenuSourceFieldsForDefault;

// Field type compatibility matrix
export const fieldTypeCompatibility = {
  STRING: ["STRING", "EMAIL", "PHONE", "URL"],
  TEXTAREA: ["STRING", "TEXTAREA"],
  PICKLIST: ["PICKLIST"],
  MULTIPICKLIST: ["MULTIPICKLIST", "PICKLIST"],
  CURRENCY: ["CURRENCY", "NUMBER", "DOUBLE", "INTEGER"],
  NUMBER: ["NUMBER", "CURRENCY", "DOUBLE", "INTEGER"],
  DOUBLE: ["DOUBLE", "NUMBER", "CURRENCY", "INTEGER"],
  INTEGER: ["INTEGER", "NUMBER", "DOUBLE", "CURRENCY"],
  DATE: ["DATE", "DATETIME"],
  DATETIME: ["DATETIME", "DATE"],
  BOOLEAN: ["BOOLEAN"],
  EMAIL: ["EMAIL", "STRING"],
  PHONE: ["PHONE"],
  URL: ["URL", "STRING"],
  REFERENCE: ["REFERENCE"],
};

// Helper function to get compatible source fields for a target field type
export const getCompatibleSourceFields = (targetFieldType, sourceFields) => {
  const compatibleTypes = fieldTypeCompatibility[targetFieldType] || [];
  return sourceFields.filter(
    (field) => compatibleTypes.includes(field.type)
  );
};

// Helper function to get mapping profile by ID
export const getMappingProfileById = (profileId) => {
  return mockMappingProfiles.find(profile => profile.id === profileId);
};

// Helper function to get default mapping profile
export const getDefaultMappingProfile = () => {
  return mockMappingProfiles.find(profile => profile.isDefault === true);
};

// Helper function to get field mappings for a profile
export const getFieldMappingsByProfileId = (profileId) => {
  return mockFieldMappings[profileId] || [];
};
